import { Link, Outlet, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Globe, Menu, X } from 'lucide-react';
import { useState } from 'react';
import { languages } from '@/types';

export default function PublicLayout() {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  
  // Get current language from localStorage or i18n
  const getCurrentLang = () => {
    if (typeof window !== 'undefined') {
      return localStorage.getItem('tickally-language') || i18n.language || 'en';
    }
    return i18n.language || 'en';
  };
  
  const [currentLang] = useState(getCurrentLang());

  const changeLanguage = (lang: string) => {
    localStorage.setItem('tickally-language', lang);
    window.location.reload();
  };

  const navLinks = [
    { path: '/', label: t('public.nav.home') },
    { path: '/how-it-works', label: t('public.nav.howItWorks') },
    { path: '/pricing', label: t('public.nav.pricing') },
    { path: '/about', label: t('public.nav.about') },
    { path: '/contact', label: t('public.nav.contact', { defaultValue: 'Contact' }) },
  ];

  const isActive = (path: string) => location.pathname === path;

  return (
    <div className="min-h-screen bg-background flex flex-col">
      {/* Header */}
      <header className="sticky top-0 z-50 w-full border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex h-16 items-center justify-between">
            {/* Logo */}
            <Link to="/" className="flex items-center space-x-2">
              <img src="/logo.png" alt="Tickally" className="h-8 w-auto" />
              <span className="text-xl font-bold text-foreground">{t('common.appName')}</span>
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden md:flex items-center space-x-8">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`text-sm font-medium transition-colors hover:text-primary ${
                    isActive(link.path) ? 'text-primary' : 'text-muted-foreground'
                  }`}
                >
                  {link.label}
                </Link>
              ))}
            </nav>

            {/* Right Side Actions */}
            <div className="hidden md:flex items-center space-x-4">
              {/* Language Selector */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="sm" className="flex items-center space-x-2">
                    <Globe className="h-4 w-4" />
                    <span className="uppercase">{currentLang}</span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  {languages.map((lang) => (
                    <DropdownMenuItem
                      key={lang.code}
                      onClick={() => changeLanguage(lang.code)}
                      className={currentLang === lang.code ? 'bg-accent' : ''}
                    >
                      {lang.name}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>

              <Link to="/org-login">
                <Button variant="ghost" size="sm">
                  {t('public.nav.login')}
                </Button>
              </Link>
              <Link to="/org-signup">
                <Button size="sm" className="bg-primary hover:bg-primary/90">
                  {t('public.nav.signup')}
                </Button>
              </Link>
            </div>

            {/* Mobile Menu Button */}
            <button
              className="md:hidden p-2"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              {mobileMenuOpen ? (
                <X className="h-6 w-6" />
              ) : (
                <Menu className="h-6 w-6" />
              )}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden border-t bg-background">
            <div className="container mx-auto px-4 py-4 space-y-4">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`block text-base font-medium ${
                    isActive(link.path) ? 'text-primary' : 'text-muted-foreground'
                  }`}
                  onClick={() => setMobileMenuOpen(false)}
                >
                  {link.label}
                </Link>
              ))}
              <div className="pt-4 border-t space-y-2">
                <p className="text-sm text-muted-foreground">{t('common.language')}</p>
                <div className="flex flex-wrap gap-2">
                  {languages.map((lang) => (
                    <button
                      key={lang.code}
                      onClick={() => changeLanguage(lang.code)}
                      className={`px-3 py-1 text-sm rounded-full border ${
                        currentLang === lang.code
                          ? 'bg-primary text-primary-foreground border-primary'
                          : 'bg-background hover:bg-accent'
                      }`}
                    >
                      {lang.name}
                    </button>
                  ))}
                </div>
              </div>
              <div className="pt-4 space-y-2">
                <Link to="/org-login" onClick={() => setMobileMenuOpen(false)}>
                  <Button variant="outline" className="w-full">
                    {t('public.nav.login')}
                  </Button>
                </Link>
                <Link to="/org-signup" onClick={() => setMobileMenuOpen(false)}>
                  <Button className="w-full bg-primary hover:bg-primary/90">
                    {t('public.nav.signup')}
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        )}
      </header>

      {/* Main Content */}
      <main className="flex-1">
        <Outlet />
      </main>

      {/* Footer */}
      <footer className="border-t bg-muted/50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            {/* Brand */}
            <div className="md:col-span-2">
              <Link to="/" className="flex items-center space-x-2 mb-4">
                <img src="/logo.png" alt="Tickally" className="h-8 w-auto" />
                <span className="text-xl font-bold">{t('common.appName')}</span>
              </Link>
              <p className="text-muted-foreground text-sm max-w-md">
                {t('public.home.hero.subtitle')}
              </p>
            </div>

            {/* Quick Links */}
            <div>
              <h4 className="font-semibold mb-4">{t('organization.title')}</h4>
              <ul className="space-y-2 text-sm">
                <li>
                  <Link to="/org-signup" className="text-muted-foreground hover:text-primary">
                    {t('public.nav.signup')}
                  </Link>
                </li>
                <li>
                  <Link to="/org-login" className="text-muted-foreground hover:text-primary">
                    {t('public.nav.login')}
                  </Link>
                </li>
                <li>
                  <Link to="/contact" className="text-muted-foreground hover:text-primary">
                    {t('public.nav.contact', { defaultValue: 'Contact' })}
                  </Link>
                </li>
              </ul>
            </div>

            {/* Members */}
            <div>
              <h4 className="font-semibold mb-4">{t('common.member')}</h4>
              <ul className="space-y-2 text-sm">
                <li>
                  <Link to="/member-signup" className="text-muted-foreground hover:text-primary">
                    {t('public.nav.signup')}
                  </Link>
                </li>
                <li>
                  <Link to="/member-login" className="text-muted-foreground hover:text-primary">
                    {t('public.nav.login')}
                  </Link>
                </li>
              </ul>
            </div>
          </div>

          <div className="border-t mt-8 pt-8 text-center text-sm text-muted-foreground">
            {t('public.footer.copyright')}
          </div>
        </div>
      </footer>
    </div>
  );
}
